import { useState } from 'react';
import { groupMessagesIntoSessions } from '../utils/conversationSessions';

const formatDuration = (ms) => {
    const totalSeconds = Math.round(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

const SMS_MAX_LENGTH = 1600; // Twilio's cap for a single concatenated message

const ConversationModal = ({ conversation, onClose, onBlockToggle, onSendSms }) => {
    // Collapsed session indices (by default every session is expanded).
    const [collapsedSessions, setCollapsedSessions] = useState(() => new Set());

    const toggleSession = (index) => {
        setCollapsedSessions((prev) => {
            const next = new Set(prev);
            next.has(index) ? next.delete(index) : next.add(index);
            return next;
        });
    };
    const [isBlocking, setIsBlocking] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [showBlockForm, setShowBlockForm] = useState(false);

    const [smsText, setSmsText] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    // Voice and SMS conversations are keyed by phone number, so we can text them
    const canSendSms = ['sms', 'voice'].includes(conversation.identifierType);

    const handleSendSms = async () => {
        if (!smsText.trim()) return;

        setIsSending(true);
        setSendResult(null);
        try {
            await onSendSms(conversation._id, smsText.trim());
            setSmsText('');
            setSendResult({ ok: true, text: 'SMS sent successfully' });
        } catch (err) {
            console.error('Error sending SMS:', err);
            setSendResult({
                ok: false,
                text: err.response?.data?.message || 'Failed to send SMS',
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleBlock = async () => {
        if (!conversation.isBlocked && !blockReason.trim()) {
            alert('Please provide a reason for blocking');
            return;
        }

        setIsBlocking(true);
        try {
            await onBlockToggle(
                conversation._id,
                !conversation.isBlocked,
                conversation.isBlocked ? '' : blockReason
            );
            setShowBlockForm(false);
            setBlockReason('');
        } catch (err) {
            console.error('Error toggling block:', err);
        } finally {
            setIsBlocking(false);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    const formatTime = (date) => {
        return new Date(date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Conversation Details</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        {/* Conversation Info */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <p className="mb-1">
                                            <strong>Brand:</strong> {conversation.brandId?.name || 'Unknown'}
                                        </p>
                                        <p className="mb-1">
                                            <strong>Customer:</strong> {conversation.identifier}
                                        </p>
                                        <p className="mb-1">
                                            <strong>Type:</strong>{' '}
                                            <span className={`badge ${conversation.identifierType === 'sms' ? 'bg-info' : conversation.identifierType === 'voice' ? 'bg-success' : 'bg-primary'}`}>
                                                {conversation.identifierType.toUpperCase()}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="mb-1">
                                            <strong>Status:</strong>{' '}
                                            <span className={`badge ${conversation.status === 'active' ? 'bg-success' :
                                                conversation.status === 'resolved' ? 'bg-secondary' : 'bg-dark'
                                                }`}>
                                                {conversation.status}
                                            </span>
                                        </p>
                                        <p className="mb-1">
                                            <strong>Started:</strong> {formatDate(conversation.createdAt)}
                                        </p>
                                        <p className="mb-1">
                                            <strong>Messages:</strong> {conversation.messageCount || conversation.messages?.length || 0}
                                        </p>
                                    </div>
                                </div>

                                {/* Blocked Status */}
                                {conversation.isBlocked && (
                                    <div className="alert alert-danger mt-3 mb-0">
                                        <strong><i className="bi bi-shield-x"></i> This conversation is blocked</strong>
                                        {conversation.blockedReason && (
                                            <p className="mb-0 mt-1">
                                                <small>Reason: {conversation.blockedReason}</small>
                                            </p>
                                        )}
                                        {conversation.blockedAt && (
                                            <p className="mb-0">
                                                <small>Blocked on: {formatDate(conversation.blockedAt)}</small>
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Messages, grouped into separate calls/sessions */}
                        <div className="conversation-messages" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {conversation.messages && conversation.messages.length > 0 ? (
                                (() => {
                                    const sessions = groupMessagesIntoSessions(conversation.messages);
                                    const sessionLabel = conversation.identifierType === 'voice' ? 'Call' : 'Session';

                                    return sessions.map((session, sIndex) => {
                                        const isCollapsed = collapsedSessions.has(sIndex);
                                        const durationMs = session.lastTs - session.startTs;

                                        return (
                                            <div key={session.callSid || sIndex} className="mb-3 border rounded">
                                                <button
                                                    type="button"
                                                    className="btn w-100 d-flex justify-content-between align-items-center bg-light border-0 rounded-top px-3 py-2"
                                                    onClick={() => toggleSession(sIndex)}
                                                    aria-expanded={!isCollapsed}
                                                >
                                                    <span className="text-start">
                                                        <strong>{sessionLabel} {sIndex + 1}</strong>
                                                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                                                            {formatDate(session.startTs)}
                                                        </span>
                                                    </span>
                                                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                        {session.messages.length} msg
                                                        {durationMs > 0 && ` · ${formatDuration(durationMs)}`}
                                                        <i className={`bi ms-2 ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
                                                    </span>
                                                </button>

                                                {!isCollapsed && (
                                                    <div className="p-3">
                                                        {session.messages.map((msg, index) => (
                                                            <div
                                                                key={index}
                                                                className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-start' : 'justify-content-end'}`}
                                                            >
                                                                <div style={{ maxWidth: '70%' }}>
                                                                    <div
                                                                        className={`p-3 rounded ${msg.role === 'user'
                                                                            ? 'bg-light border'
                                                                            : msg.metadata?.sentBy
                                                                                ? 'bg-success text-white'
                                                                                : 'bg-primary text-white'
                                                                            }`}
                                                                    >
                                                                        <div className="mb-1">
                                                                            <strong className="text-uppercase" style={{ fontSize: '0.75rem' }}>
                                                                                {msg.role === 'user' ? 'Customer' : msg.metadata?.sentBy ? 'Admin' : 'Assistant'}
                                                                            </strong>
                                                                            <span className="ms-2" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                                                                {formatTime(msg.timestamp)}
                                                                            </span>
                                                                        </div>
                                                                        <div style={{ whiteSpace: 'pre-wrap' }}>
                                                                            {msg.content}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()
                            ) : (
                                <p className="text-muted text-center">No messages in this conversation</p>
                            )}
                        </div>

                        {/* Send SMS Section (phone-backed conversations only) */}
                        {canSendSms && (
                            <div className="mt-4 pt-3 border-top">
                                <h6 className="mb-2">
                                    <i className="bi bi-chat-left-text me-2"></i>
                                    Send SMS to {conversation.identifier}
                                </h6>
                                {conversation.isBlocked ? (
                                    <p className="text-muted small mb-0">
                                        This conversation is blocked. Unblock it below to send messages.
                                    </p>
                                ) : (
                                    <>
                                        <textarea
                                            className="form-control mb-2"
                                            rows="3"
                                            maxLength={SMS_MAX_LENGTH}
                                            value={smsText}
                                            onChange={(e) => setSmsText(e.target.value)}
                                            placeholder="e.g., Thanks for trying our service! We recently updated our knowledge base — you're welcome to try the new, more accurate version."
                                            disabled={isSending}
                                        />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted">
                                                {smsText.length}/{SMS_MAX_LENGTH}
                                            </small>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleSendSms}
                                                disabled={isSending || !smsText.trim()}
                                            >
                                                {isSending ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-send me-2"></i>
                                                        Send SMS
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                        {sendResult && (
                                            <div className={`alert ${sendResult.ok ? 'alert-success' : 'alert-danger'} mt-2 mb-0 py-2`}>
                                                {sendResult.text}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Block/Unblock Section */}
                        <div className="mt-4 pt-3 border-top">
                            {!showBlockForm ? (
                                <button
                                    className={`btn ${conversation.isBlocked ? 'btn-success' : 'btn-danger'} w-100`}
                                    onClick={() => {
                                        if (conversation.isBlocked) {
                                            // Unblock immediately
                                            handleBlock();
                                        } else {
                                            // Show form for blocking
                                            setShowBlockForm(true);
                                        }
                                    }}
                                    disabled={isBlocking}
                                >
                                    {isBlocking ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className={`bi ${conversation.isBlocked ? 'bi-shield-check' : 'bi-shield-x'} me-2`}></i>
                                            {conversation.isBlocked ? 'Unblock Conversation' : 'Block Conversation'}
                                        </>
                                    )}
                                </button>
                            ) : (
                                <div className="card">
                                    <div className="card-body">
                                        <h6 className="card-title">Block Conversation</h6>
                                        <p className="text-muted small">
                                            Blocking will prevent this user from sending new messages.
                                        </p>
                                        <div className="mb-3">
                                            <label className="form-label">Reason for blocking:</label>
                                            <textarea
                                                className="form-control"
                                                rows="3"
                                                value={blockReason}
                                                onChange={(e) => setBlockReason(e.target.value)}
                                                placeholder="e.g., Spam, abuse, inappropriate content..."
                                            />
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-danger flex-grow-1"
                                                onClick={handleBlock}
                                                disabled={isBlocking || !blockReason.trim()}
                                            >
                                                {isBlocking ? 'Blocking...' : 'Confirm Block'}
                                            </button>
                                            <button
                                                className="btn btn-secondary"
                                                onClick={() => {
                                                    setShowBlockForm(false);
                                                    setBlockReason('');
                                                }}
                                                disabled={isBlocking}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConversationModal;
